import React, { useRef, useCallback, useEffect } from 'react';
import { useKeyboard } from './KeyboardContext';

const keyStyle = {
    background: '#2C2C2E',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
};

const specialKeyStyle = {
    background: '#3A3A3C',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
};

const doneKeyStyle = {
    background: '#48484A',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
    border: '1px solid rgba(234, 234, 234, 0.12)',
};

/**
 * Phone-style pad for winners table editing.
 * Layout: digits, HOME, BACKSPACE, arrows, comma, Done.
 */
const WinnersPadKeyboard = () => {
    const { handleKeyPress, closeKeyboard } = useKeyboard();

    const bsTimer = useRef(null);
    const bsCount = useRef(0);

    const scheduleNextBackspace = useCallback(() => {
        bsCount.current += 1;
        const count = bsCount.current;
        let delay = 150;
        if (count >= 3) delay = 100;
        if (count >= 6) delay = 70;
        if (count >= 10) delay = 50;
        if (count >= 18) delay = 35;
        if (count >= 30) delay = 25;

        bsTimer.current = setTimeout(() => {
            handleKeyPress('BACKSPACE');
            scheduleNextBackspace();
        }, delay);
    }, [handleKeyPress]);

    const startBackspaceRepeat = useCallback(() => {
        bsCount.current = 0;
        bsTimer.current = setTimeout(() => {
            handleKeyPress('BACKSPACE');
            scheduleNextBackspace();
        }, 400);
    }, [handleKeyPress, scheduleNextBackspace]);

    const stopBackspaceRepeat = useCallback(() => {
        if (bsTimer.current) {
            clearTimeout(bsTimer.current);
            bsTimer.current = null;
        }
        bsCount.current = 0;
    }, []);

    useEffect(() => () => stopBackspaceRepeat(), [stopBackspaceRepeat]);

    const renderDigit = (digit) => (
        <button
            key={digit}
            type="button"
            onClick={() => handleKeyPress(digit)}
            className="flex-1 h-[52px] rounded-[10px] flex items-center justify-center
                active:scale-[0.97] transition-all duration-100"
            style={keyStyle}
        >
            <span className="text-white text-[26px] font-medium leading-none">{digit}</span>
        </button>
    );

    const renderIconButton = (key, onPress, children, style = specialKeyStyle) => (
        <button
            key={key}
            type="button"
            onClick={onPress}
            className="flex-1 h-[52px] rounded-[10px] flex items-center justify-center
                active:scale-[0.97] transition-all duration-100"
            style={style}
        >
            {children}
        </button>
    );

    return (
        <div className="w-full px-2 pb-12 bg-[#1A1A1C]">
            <div className="flex flex-col gap-[7px]">
                {/* Row 1 */}
                <div className="flex flex-row gap-[7px]">
                    {renderDigit('1')}
                    {renderDigit('2')}
                    {renderDigit('3')}
                    {renderIconButton('HOME', () => handleKeyPress('ROW_FIRST'), (
                        <svg width="22" height="18" viewBox="0 0 22 18" fill="none" aria-hidden="true">
                            <path d="M8 2V16" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" />
                            <path d="M18 9H10M10 9L13.5 5.5M10 9L13.5 12.5" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ))}
                </div>

                {/* Row 2 */}
                <div className="flex flex-row gap-[7px]">
                    {renderDigit('4')}
                    {renderDigit('5')}
                    {renderDigit('6')}
                    <button
                        type="button"
                        className="flex-1 h-[52px] rounded-[10px] flex items-center justify-center
                            active:scale-[0.97] transition-all duration-100"
                        style={specialKeyStyle}
                        onTouchStart={() => { handleKeyPress('BACKSPACE'); startBackspaceRepeat(); }}
                        onTouchEnd={stopBackspaceRepeat}
                        onTouchCancel={stopBackspaceRepeat}
                        onMouseDown={() => { handleKeyPress('BACKSPACE'); startBackspaceRepeat(); }}
                        onMouseUp={stopBackspaceRepeat}
                        onMouseLeave={stopBackspaceRepeat}
                    >
                        <svg width="28" height="22" viewBox="0 0 28 20" fill="none" aria-hidden="true">
                            <path
                                d="M8.5 2L2 10L8.5 18H26V2H8.5Z"
                                stroke="#EAEAEA"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M12 7L19 13M19 7L12 13"
                                stroke="#EAEAEA"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>

                {/* Row 3 */}
                <div className="flex flex-row gap-[7px]">
                    {renderDigit('7')}
                    {renderDigit('8')}
                    {renderDigit('9')}
                    {renderIconButton(',', () => handleKeyPress(','), (
                        <span className="text-white text-[28px] font-medium leading-none">,</span>
                    ), keyStyle)}
                </div>

                {/* Row 4 */}
                <div className="flex flex-row gap-[7px]">
                    {renderIconButton('LEFT', () => handleKeyPress('ROW_UP'), (
                        <svg width="22" height="16" viewBox="0 0 22 16" fill="none" aria-hidden="true">
                            <path d="M20 8H4M4 8L9 3M4 8L9 13" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ))}
                    {renderDigit('0')}
                    {renderIconButton('RIGHT', () => handleKeyPress('ROW_DOWN'), (
                        <svg width="22" height="16" viewBox="0 0 22 16" fill="none" aria-hidden="true">
                            <path d="M2 8H18M18 8L13 3M18 8L13 13" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ))}
                    <button
                        type="button"
                        onClick={() => closeKeyboard()}
                        className="flex-1 h-[52px] rounded-[10px] flex items-center justify-center
                            active:scale-[0.97] transition-all duration-100"
                        style={doneKeyStyle}
                    >
                        <span className="text-white text-[17px] font-semibold">Done</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WinnersPadKeyboard;
